import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Globe, Loader2, Pin, RefreshCw, Trash2, Upload, X } from 'lucide-react';
import { Card } from '@/components/ui';
import { ApiError } from '@/lib/api';
import {
  deleteAuthority,
  fetchAuthorityFromUrl,
  listAuthorities,
  listMatters,
  pinAuthority,
  reingestAuthority,
  unpinAuthority,
  uploadAuthority,
  type AuthorityIngestResult,
  type AuthorityMetadataInput,
  type AuthoritySummary,
  type AuthorityType,
} from '@/lib/api-client';

// Legal Brain Phase A3: the verified authorities corpus. Every citation the
// model gives comes from this page's ingested source text — legislation
// section by section, judgments paragraph by paragraph. Dark stone + amber,
// matching the legal skills page.

const JURISDICTIONS = ['VIC', 'CTH', 'HCA', 'FCA', 'FCFCOA', 'VSC', 'VSCA', 'VCC', 'MCV', 'VCAT'];
const WHITELIST_NOTE = 'legislation.vic.gov.au · legislation.gov.au · classic.austlii.edu.au';

const inputClass =
  'w-full rounded-md border border-stone-800 bg-stone-900 px-3 py-2 text-sm text-stone-200 placeholder:text-stone-600 focus:border-amber-700/60 focus:outline-none';
const labelClass = 'mb-1 block text-[10px] uppercase tracking-[0.2em] text-stone-500';

interface FormState {
  mode: 'upload' | 'url';
  type: AuthorityType;
  jurisdiction: string;
  title: string;
  citation: string;
  provisionOrParagraph: string;
  url: string;
}

const EMPTY: FormState = {
  mode: 'upload',
  type: 'LEGISLATION',
  jurisdiction: 'VIC',
  title: '',
  citation: '',
  provisionOrParagraph: '',
  url: '',
};

function describeResult(r: AuthorityIngestResult): string {
  const what = `${r.chunks} chunk${r.chunks === 1 ? '' : 's'}`;
  const embed = r.embedded ? 'embedded' : `stored without vectors (${r.embedError ?? 'embed pending'})`;
  switch (r.action) {
    case 'created':
      return `Added "${r.authority.citation}" — ${what}, ${embed}.`;
    case 'replaced':
      return `Updated text of "${r.authority.citation}" — re-chunked (${what}), ${embed}.`;
    case 'metadata-updated':
      return `"${r.authority.citation}" already held (identical text); metadata refreshed.`;
    default:
      return `"${r.authority.citation}" already held (identical text); nothing changed.`;
  }
}

export function AdminAuthorities() {
  const [authorities, setAuthorities] = useState<AuthoritySummary[]>([]);
  const [matters, setMatters] = useState<Array<{ id: string; name: string }>>([]);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pinPicker, setPinPicker] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  async function refresh(): Promise<void> {
    setAuthorities(await listAuthorities().catch(() => []));
  }
  useEffect(() => {
    void refresh();
    void listMatters()
      .then((ms) => setMatters(ms.map((m) => ({ id: m.id, name: m.name }))))
      .catch(() => setMatters([]));
  }, []);

  async function handleIngest(): Promise<void> {
    setError(null);
    setNotice(null);
    if (!form.citation.trim() || !form.jurisdiction.trim()) {
      setError('Citation and jurisdiction are required.');
      return;
    }
    if (form.mode === 'upload' && !file) {
      setError('Choose a PDF, DOCX, TXT or HTML file.');
      return;
    }
    if (form.mode === 'upload' && !form.title.trim()) {
      setError('Title is required for file uploads.');
      return;
    }
    if (form.mode === 'url' && !form.url.trim()) {
      setError('Enter a URL on the whitelist.');
      return;
    }
    setBusy(true);
    try {
      const meta: AuthorityMetadataInput = {
        type: form.type,
        jurisdiction: form.jurisdiction.trim(),
        title: form.title.trim(),
        citation: form.citation.trim(),
        ...(form.provisionOrParagraph.trim()
          ? { provisionOrParagraph: form.provisionOrParagraph.trim() }
          : {}),
      };
      const result =
        form.mode === 'upload'
          ? await uploadAuthority(file!, meta)
          : await fetchAuthorityFromUrl(form.url.trim(), meta);
      setNotice(describeResult(result));
      setForm((f) => ({ ...EMPTY, mode: f.mode, type: f.type, jurisdiction: f.jurisdiction }));
      setFile(null);
      if (fileInput.current) {
        fileInput.current.value = '';
      }
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Ingestion failed.');
    } finally {
      setBusy(false);
    }
  }

  async function handleReingest(a: AuthoritySummary): Promise<void> {
    setError(null);
    setNotice(null);
    setRowBusy(a.id);
    try {
      const result = await reingestAuthority(a.id);
      setNotice(describeResult(result));
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Re-ingest failed.');
    } finally {
      setRowBusy(null);
    }
  }

  async function handleDelete(a: AuthoritySummary): Promise<void> {
    if (
      !window.confirm(
        `Delete "${a.citation}" and its ${a.chunkCount} chunks? Answers will no longer be able to cite it.`,
      )
    ) {
      return;
    }
    setError(null);
    setRowBusy(a.id);
    try {
      await deleteAuthority(a.id);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Delete failed.');
    } finally {
      setRowBusy(null);
    }
  }

  async function handlePin(a: AuthoritySummary, matterId: string): Promise<void> {
    setError(null);
    setPinPicker(null);
    try {
      await pinAuthority(a.id, matterId);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Pin failed.');
    }
  }

  async function handleUnpin(a: AuthoritySummary, matterId: string): Promise<void> {
    setError(null);
    try {
      await unpinAuthority(a.id, matterId);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Unpin failed.');
    }
  }

  return (
    <div className="min-h-screen bg-stone-950 px-5 py-6 sm:px-6">
      <div className="mx-auto w-full max-w-4xl space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl italic text-stone-100">Authorities</h1>
          <Link
            to="/hearing"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Hearing
          </Link>
        </div>
        <p className="text-sm text-stone-400">
          The verified legal sources the model is allowed to cite. Legislation is chunked section
          by section, judgments paragraph by paragraph — citations in answers quote this text,
          never model memory. Pin an authority to a matter to boost it in that matter&apos;s answers.
        </p>

        <Card variant="default" padding="md" className="space-y-4 border-amber-900/30 bg-amber-950/10">
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">Add authority</p>
            <div className="flex gap-1.5">
              <ModeButton
                active={form.mode === 'upload'}
                onClick={() => setForm((f) => ({ ...f, mode: 'upload' }))}
                icon={<Upload className="h-3 w-3" />}
                label="File"
              />
              <ModeButton
                active={form.mode === 'url'}
                onClick={() => setForm((f) => ({ ...f, mode: 'url' }))}
                icon={<Globe className="h-3 w-3" />}
                label="Fetch URL"
              />
            </div>
          </div>

          {form.mode === 'upload' ? (
            <div>
              <label className={labelClass} htmlFor="au-file">
                File (PDF, DOCX, TXT, HTML)
              </label>
              <input
                id="au-file"
                ref={fileInput}
                type="file"
                accept=".pdf,.docx,.txt,.html,.htm,.md"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-stone-400 file:mr-3 file:rounded-md file:border-0 file:bg-stone-800 file:px-3 file:py-1.5 file:text-sm file:text-stone-200 hover:file:bg-stone-700"
              />
            </div>
          ) : (
            <div>
              <label className={labelClass} htmlFor="au-url">
                Single document page URL ({WHITELIST_NOTE})
              </label>
              <input
                id="au-url"
                type="url"
                value={form.url}
                onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                placeholder="https://www.legislation.vic.gov.au/in-force/acts/evidence-act-2008/..."
                className={inputClass}
              />
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className={labelClass} htmlFor="au-type">
                Type
              </label>
              <select
                id="au-type"
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as AuthorityType }))}
                className={inputClass}
              >
                <option value="LEGISLATION">Legislation</option>
                <option value="CASE">Case</option>
              </select>
            </div>
            <div>
              <label className={labelClass} htmlFor="au-jur">
                Jurisdiction
              </label>
              <select
                id="au-jur"
                value={form.jurisdiction}
                onChange={(e) => setForm((f) => ({ ...f, jurisdiction: e.target.value }))}
                className={inputClass}
              >
                {JURISDICTIONS.map((j) => (
                  <option key={j} value={j}>
                    {j}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass} htmlFor="au-prov">
                Provision / paragraphs (optional)
              </label>
              <input
                id="au-prov"
                type="text"
                value={form.provisionOrParagraph}
                onChange={(e) => setForm((f) => ({ ...f, provisionOrParagraph: e.target.value }))}
                placeholder="s 55, or [12]-[30]"
                className={inputClass}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass} htmlFor="au-title">
                Title{form.mode === 'url' ? ' (blank = page title)' : ''}
              </label>
              <input
                id="au-title"
                type="text"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Evidence Act 2008"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="au-cite">
                Citation
              </label>
              <input
                id="au-cite"
                type="text"
                value={form.citation}
                onChange={(e) => setForm((f) => ({ ...f, citation: e.target.value }))}
                placeholder="Evidence Act 2008 (Vic), or [2000] HCA 63"
                className={inputClass}
              />
            </div>
          </div>

          {error ? <p className="text-sm text-red-400">{error}</p> : null}
          {notice ? <p className="text-sm text-emerald-400">{notice}</p> : null}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => void handleIngest()}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-stone-950 transition-colors hover:bg-amber-700 disabled:opacity-40"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : form.mode === 'upload' ? (
                <Upload className="h-4 w-4" aria-hidden />
              ) : (
                <Globe className="h-4 w-4" aria-hidden />
              )}
              {form.mode === 'upload' ? 'Ingest file' : 'Fetch & ingest'}
            </button>
          </div>
        </Card>

        <section className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
            Corpus ({authorities.length})
          </p>
          {authorities.length === 0 ? (
            <p className="text-sm text-stone-500">
              No authorities yet. Until sources are ingested the model must flag legal propositions
              as unverified rather than cite them.
            </p>
          ) : (
            <div className="space-y-2">
              {authorities.map((a) => (
                <div
                  key={a.id}
                  className="space-y-2 rounded-md border border-stone-800/60 bg-stone-900/60 px-3 py-2"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-stone-200">
                        {a.title}
                        {a.provisionOrParagraph ? (
                          <span className="text-stone-500"> · {a.provisionOrParagraph}</span>
                        ) : null}
                      </p>
                      <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">
                        <span className={a.type === 'CASE' ? 'text-sky-500' : 'text-amber-500'}>
                          {a.type === 'CASE' ? 'Case' : 'Legislation'}
                        </span>{' '}
                        · {a.citation} · {a.jurisdiction} · {a.chunkCount} chunks
                        {a.embeddedCount < a.chunkCount
                          ? ` (${a.chunkCount - a.embeddedCount} awaiting vectors)`
                          : ''}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setPinPicker(pinPicker === a.id ? null : a.id)}
                        aria-label="Pin to matter"
                        title="Pin to matter"
                        className="rounded p-1 text-stone-500 transition-colors hover:text-amber-400"
                      >
                        <Pin className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleReingest(a)}
                        disabled={rowBusy === a.id}
                        aria-label="Re-ingest"
                        title={a.sourceUrl ? 'Re-fetch from source and re-ingest' : 'Re-chunk stored text'}
                        className="rounded p-1 text-stone-500 transition-colors hover:text-amber-400 disabled:opacity-40"
                      >
                        {rowBusy === a.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(a)}
                        disabled={rowBusy === a.id}
                        aria-label="Delete authority"
                        className="rounded p-1 text-stone-500 transition-colors hover:text-red-400 disabled:opacity-40"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {a.pins.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {a.pins.map((p) => (
                        <span
                          key={p.matterId}
                          className="inline-flex items-center gap-1 rounded border border-amber-700/60 bg-amber-950/30 px-2 py-0.5 text-[11px] text-amber-300"
                        >
                          <Pin className="h-3 w-3" />
                          {p.matterName}
                          <button
                            type="button"
                            onClick={() => void handleUnpin(a, p.matterId)}
                            aria-label={`Unpin from ${p.matterName}`}
                            className="text-amber-500/70 hover:text-red-400"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : null}

                  {pinPicker === a.id ? (
                    <div className="flex flex-wrap gap-1.5">
                      {matters.filter((m) => !a.pins.some((p) => p.matterId === m.id)).length === 0 ? (
                        <p className="text-[11px] text-stone-500">Pinned to every matter already.</p>
                      ) : (
                        matters
                          .filter((m) => !a.pins.some((p) => p.matterId === m.id))
                          .map((m) => (
                            <button
                              key={m.id}
                              type="button"
                              onClick={() => void handlePin(a, m.id)}
                              className="rounded border border-stone-800 px-2 py-0.5 text-[11px] text-stone-400 hover:border-amber-700/60 hover:text-amber-300"
                            >
                              + {m.name}
                            </button>
                          ))
                      )}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function ModeButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? 'inline-flex items-center gap-1 rounded border border-amber-700/60 bg-amber-950/30 px-2 py-0.5 text-[11px] text-amber-300'
          : 'inline-flex items-center gap-1 rounded border border-stone-800 px-2 py-0.5 text-[11px] text-stone-500 hover:border-stone-700'
      }
    >
      {icon}
      {label}
    </button>
  );
}
